import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Dropdown, Menu, Tooltip } from 'antd';
import {
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

import { PRODUCT_VIEW } from 'static/Constants';

import { RibbonButton } from 'common/components';
import { useQueries } from '@tanstack/react-query';

import {
  useCheckIsSuperAdmin,
  useCheckMemberSupplier,
  useCheckIsSuperMember,
} from 'hooks';
import { hasPermission, useCheckPermissions } from 'hooks/useCheckPermissions';
import {
  ABILITY_SUBJECT,
  PERMISSION_VIEW_QA_SPECIFICATION,
} from 'static/Permission';

import messages from 'i18n/messages/home';
import messagesProduct from 'i18n/messages/product';
import * as ssoProductSelectors from 'pages/sso-product/controllers/selectors';
import AddEditQaSpecTemplate from '../product/AddEditQaSpecTemplate';
import { getQaSpecHeaderKey } from 'pages/qa-spec/query';
import ImportQaSpecBulkEditModal from '../../modal/bulk-edit-product/ImportQaSpecBulkEditModal';
import * as qaSpecServices from 'services/qaSpec';

const { SubMenu } = Menu;

/* Allowed Types for QA Spec Template Currently only Food has QA Spec Template, 
 we can add more types in the future if needed */
const ALLOWED_QA_SPEC_TYPES = ['Food', 'Non-Food', 'HBC / OTC', 'Pet'];

const QaSpecTemplateAction = ({
  productFull,
  productId,
  selectedProducts,
  isProductDetail = false,
}) => {
  const location = useLocation();
  const isSuperAdmin = useCheckIsSuperAdmin();
  const isMemberSupplierUser = useCheckMemberSupplier();
  const isSuperMember = useCheckIsSuperMember();
  const [visibleImportModal, setVisibleImportModal] = useState(false);

  const hasPermissionUploadQaSpecTemplate = hasPermission(
    ABILITY_SUBJECT.UPLOAD_QA_SPECIFICATION_TEMPLATE,
  );

  const isAllowedQaSpecTemplateAction =
    isSuperAdmin || isMemberSupplierUser || hasPermissionUploadQaSpecTemplate;

  // -------------------------------
  // Normalize products
  // -------------------------------
  const rawProducts = selectedProducts?.length
    ? selectedProducts
    : productFull
      ? [productFull]
      : [];

  const products = rawProducts.map((p) => ({
    ...p,
    productId: p.productId ?? p.id,
  }));

  // Use normalized productId for queries
  const productIds = products.map((p) => Number(p.productId)).filter(Boolean);

  // -------------------------------
  // QA Spec Headers Queries
  // -------------------------------
  const hasPermissionViewQaSpec = useCheckPermissions(
    PERMISSION_VIEW_QA_SPECIFICATION,
  );

  const qaSpecHeaderQueries = useQueries({
    queries: productIds.map((id) => ({
      queryKey: getQaSpecHeaderKey(id),
      queryFn: async () => {
        const response = await qaSpecServices.getQaSpecDataHeader({
          productId: id,
        });
        return response?.data;
      },
      enabled: productIds.length > 0 && hasPermissionViewQaSpec,
    })),
  });

  const qaSpecHeaders = qaSpecHeaderQueries.map((q) => q.data).filter(Boolean);

  // -------------------------------
  // Compute Approved Products
  // -------------------------------
  const nonSubmittedStatuses = new Set(
    ['Not Submitted', 'Initiated', 'Unlocked', 'Rejected', 'Not Started'].map(
      (s) => s.toLowerCase(),
    ),
  );

  const approvedProducts = qaSpecHeaders
    ?.filter(({ status }) => !nonSubmittedStatuses.has(status?.toLowerCase()))
    .map(({ productId }) => `RP${productId}`);

  const isUploadDisabled = approvedProducts.length > 0;

  const uploadDisabledReason =
    approvedProducts.length > 0
      ? `${approvedProducts.join(
          ', ',
        )} QA Spec is Locked. Please request Owner unlock spec to edit in order to use template. To proceed please unselect the Products with QA spec status as Approved & Submitted.`
      : '';

  // -------------------------------
  // Dropdown / Tooltip Logic
  // -------------------------------
  const noProductsSelected = products.length === 0;

  const invalidType = !products.every((p) =>
    ALLOWED_QA_SPEC_TYPES.includes(p.qaSpecType ?? p.type),
  );

  const isBpSsoProductValidate = useSelector(
    ssoProductSelectors.selectIsBpSsoProductValidate(),
  );

  const validateOverview = () => {
    const regex = /\/product\/\d+$/;
    const isOverview = regex.test(location.pathname);
    return isOverview ? PRODUCT_VIEW.OVERVIEW : null;
  };

  const getActiveContent = () => {
    const regex = /\/(?:product)\/(.+)(?=\/\d+$)/;
    const results = location?.pathname?.match(regex);

    return results?.length
      ? results[1]
      : isBpSsoProductValidate
        ? PRODUCT_VIEW.OVERVIEW
        : validateOverview();
  };

  const activeContent = getActiveContent();

  const isActiveView = [
    PRODUCT_VIEW.DETAILVIEW,
    PRODUCT_VIEW.OVERVIEW,
    PRODUCT_VIEW.QA_SPEC,
    PRODUCT_VIEW.QA_EVALUATION_FORM,
  ].includes(activeContent);

  const dropdownDisabledReason =
    // noProductsSelected
    //   ? 'Please select at least one product.'
    // :
    invalidType
      ? `QA Spec Template is only available for type: ${ALLOWED_QA_SPEC_TYPES.join(', ')}.`
      : '';

  const disabledDropdown =
    !!dropdownDisabledReason ||
    (!isActiveView &&
      !isSuperAdmin &&
      !isSuperMember &&
      !isAllowedQaSpecTemplateAction);

  // -------------------------------
  // Handlers
  // -------------------------------
  const onClickImportButton = () => setVisibleImportModal(true);

  // -------------------------------
  // Menu Options
  // -------------------------------
  const menuOptions = (
    <Menu>
      {/* Download */}
      <SubMenu
        key='upload-download-qa-spec-template'
        style={{ backgroundColor: 'inherit' }}
        disabled={noProductsSelected}
        title={
          // <Tooltip title={noProductsSelected ? "No products selected" : undefined}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 6,
              alignItems: 'center',
              marginRight: 8,
            }}
          >
            <RibbonButton
              size='small'
              className='button-item-dropdown'
              icon={<DownloadOutlined />}
              label={messagesProduct.downloadTemplate}
              disabled={noProductsSelected}
            />
            {noProductsSelected && (
              <Tooltip
                placement='top'
                title={'No products selected'}
                color='#238dd9'
              >
                <InfoCircleOutlined className='member-defined-property__wrong-field-type-notice' />
              </Tooltip>
            )}
          </div>
          //</Tooltip>
        }
      >
        <AddEditQaSpecTemplate selectedProducts={productIds} isProductDetail />
      </SubMenu>

      {/* Upload */}
      <Menu.Item
        key='upload-qa-spec-template'
        style={{ backgroundColor: 'inherit' }}
        title={messagesProduct.assignCategoryToProduct}
        onClick={!isUploadDisabled ? onClickImportButton : undefined}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 6,
            alignItems: 'center',
          }}
        >
          <RibbonButton
            size='small'
            className='button-item-dropdown'
            icon={<UploadOutlined />}
            label={messagesProduct.uploadTemplate}
            disabled={isUploadDisabled}
          />
          {isUploadDisabled && (
            <Tooltip
              placement='top'
              title={uploadDisabledReason}
              color='#238dd9'
            >
              <InfoCircleOutlined className='member-defined-property__wrong-field-type-notice' />
            </Tooltip>
          )}
        </div>
      </Menu.Item>
    </Menu>
  );

  // -------------------------------
  // Render
  // -------------------------------

  const [isQaSpecTemplateVisible, setIsQaSpecTemplateVisible] = useState(true);
  useEffect(() => {
    const checkTemplateVisibility = async () => {
      const response = await qaSpecServices.checkQaSpecTemplateVisibility({
        productId: productIds,
      });

      if (response) {
        setIsQaSpecTemplateVisible(response.data);
      }
    };

    if (productIds.length > 0 && disabledDropdown == false) {
      checkTemplateVisibility();
    } else if (productIds.length === 0) {
      setIsQaSpecTemplateVisible(true);
    }
  }, [productIds, disabledDropdown]);

  return (
    <>
      {isQaSpecTemplateVisible && (
        <Tooltip placement='top' title={dropdownDisabledReason} color='#238dd9'>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Dropdown
              overlay={menuOptions}
              placement='bottomLeft'
              disabled={disabledDropdown}
              arrow
            >
              <RibbonButton
                icon={<FileExcelOutlined />}
                label={messages.qaSpecTemplate}
              />
            </Dropdown>

            {dropdownDisabledReason && (
              <InfoCircleOutlined
                style={{ marginLeft: 1, padding: 0 }}
                className='member-defined-property__wrong-field-type-notice'
              />
            )}
          </div>
        </Tooltip>
      )}

      {visibleImportModal && (
        <ImportQaSpecBulkEditModal
          visible={visibleImportModal}
          setVisible={setVisibleImportModal}
          isProductDetail={isProductDetail}
        />
      )}
    </>
  );
};

export default QaSpecTemplateAction;
